import { HttpAgent } from "@ag-ui/client";
import { getServiceConfig } from "@/config/serviceConfig";
import { getOrCreateSessionId } from "@/features/catalog_case_review/shared/services/caseLockService";
import { PAGE_AGENT_NAME } from "../constants/pageAgentIds";

/** The `localStorage` key the axios interceptor reads the bearer token from. */
const ACCESS_TOKEN_STORAGE_KEY = "access_token";

/** The run route, relative to the case-db base URL; `authenticatedFetch` prepends the base. */
export const RUN_ROUTE = `/api/v1/agui/${PAGE_AGENT_NAME}/run`;

/** Both media types: the stream on success, JSON for every pre-stream failure. */
export const RUN_ACCEPT = "text/event-stream, application/json";

/**
 * The browser's end of the AG-UI transport.
 *
 * `HttpAgent.requestInit` hard-sets `Accept: text/event-stream` after spreading configured headers,
 * so the `fetch` seam is the one place that can ask for JSON too — and every pre-stream failure
 * (400, 404, 409, 503) is JSON. The same seam carries the bearer token, read per request because
 * tokens expire, and the per-tab `X-Session-Id` the case-db controllers resolve the actor from.
 */
export class AguiClient {
  /**
   * The agent object for one conversation — one per thread, never per turn. `threadId`, the message
   * list and the `AbortController` are instance state on `HttpAgent`.
   */
  pageAgent(threadId: string): HttpAgent {
    return new HttpAgent({ url: RUN_ROUTE, threadId, fetch: authenticatedFetch });
  }
}

/** `window.fetch` against the case-db service with the headers the run route needs. */
export async function authenticatedFetch(url: string, init: RequestInit): Promise<Response> {
  const { caseDb } = await getServiceConfig();
  if (!caseDb) throw new Error("case-db service URL is not configured");
  const headers = new Headers(init.headers);
  headers.set("Accept", RUN_ACCEPT);
  const token = localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  headers.set("X-Session-Id", getOrCreateSessionId());
  return fetch(`${caseDb}${url}`, { ...init, headers });
}

export const aguiClient = new AguiClient();
